import { compareLabel } from '~/util/compareLabel'

/** Value-keyed chart color resolver (spec §4.1 D3).
 *
 *  Slot = index of the dim value in its FULL catalog-order value list (the
 *  controls bundle's dims_view values, compareLabel-sorted — the same order the
 *  filter panel shows), modulo the palette. Built over the full set — NEVER the
 *  filtered/visible subset — so filtering can never repaint surviving series.
 *
 *  Overflow rule: keys present in chart data but missing from the catalog get
 *  deterministic slots after the catalog block (compareLabel-sorted), with ONE
 *  console.warn — a lagging catalog must not crash a chart, but it must not be
 *  silent either. Values >palette-length wrap via stable modulo (documented
 *  deviation, spec §3.1). */
export function makeColorForKey(
  catalogValues: string[],
  extraKeys: string[],
  palette: string[],
): (key: string) => string | undefined {
  const map = new Map<string, string>()
  catalogValues.forEach((v, i) => {
    if (!map.has(v)) map.set(v, palette[i % palette.length]!)
  })
  const overflow = [...new Set(extraKeys)].filter((k) => !map.has(k)).sort(compareLabel)
  if (overflow.length > 0) {
    console.warn(
      `dimValueColors: ${overflow.length} value(s) in chart data missing from the dim catalog — assigning overflow slots`,
      overflow,
    )
    overflow.forEach((k, j) => {
      map.set(k, palette[(catalogValues.length + j) % palette.length]!)
    })
  }
  // Wave-3 contract (spec §4): unknown key -> undefined, no brand-slot collapse. Callers
  // (assignGroups) fall back to a POSITIONAL palette slot instead.
  return (key: string) => map.get(key)
}

const seenCatalogOrder = new Map<string, { factTable: string; sig: string }>()

/** Wave-3 §6: cheap stand-in for the parked canonical cross-fact dim catalog. Cross-page
 *  color identity for a shared dim value holds only while every fact's catalog orders the
 *  dim's values identically — warn (dev only) the moment that assumption breaks. */
export function warnOnCrossFactCatalogDivergence(
  dim: string,
  factTable: string,
  values: string[],
  isDev: boolean = import.meta.env.DEV,
): void {
  if (!isDev || !dim || values.length === 0) return
  const sig = values.join(String.fromCharCode(0)) // NUL join — dim values can contain spaces
  const seen = seenCatalogOrder.get(dim)
  if (!seen) {
    seenCatalogOrder.set(dim, { factTable, sig })
    return
  }
  if (seen.factTable !== factTable && seen.sig !== sig) {
    console.warn(
      `dimValueColors: dim "${dim}" catalog order differs between ${seen.factTable} and ${factTable} — cross-page color identity may diverge`,
    )
  }
}
